<template>
    <div class="toggle-switch" :class="containerClass">
        <button
            v-for="(option, index) in options"
            :key="`${index}-${String(option.value)}`"
            @click="emit('update:modelValue', option.value)"
            class="toggle-switch-button"
            :data-edge="index === 0 ? 'left' : index === options.length - 1 ? 'right' : 'middle'"
            :class="[buttonClass(option.value)]"
        >
            {{ option.label }}
        </button>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Option {
    label: string;
    value: string | number | boolean;
}

interface Props {
    modelValue: string | number | boolean;
    options: Option[];
    variant?: 'pill' | 'bordered';
}

const props = withDefaults(defineProps<Props>(), {
    variant: 'pill',
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string | number | boolean): void;
}>();

const containerClass = computed(() => {
    if (props.variant === 'bordered') {
        return 'toggle-switch-bordered flex items-center justify-center';
    }
    return 'toggle-switch-pill flex items-center justify-center bg-white rounded-full p-1 w-fit mx-auto shadow-sm';
});

function buttonClass(value: string | number | boolean) {
    const isActive = props.modelValue === value;

    if (props.variant === 'bordered') {
        return [
            'border border-gray-300 px-4 py-2 transition-all duration-300 ease-out',
            isActive ? 'bg-primary text-white shadow-md hover:shadow-lg' : 'bg-white text-dark hover:bg-primary-light',
        ];
    }

    return [
        'px-6 py-2 rounded-full font-medium transition-all duration-300 ease-out',
        isActive ? 'bg-primary text-white shadow-md hover:shadow-lg' : 'bg-transparent text-dark hover:bg-gray-100',
    ];
}
</script>

<style scoped>
/* Animation de sliding pour les switches toggle */
@keyframes slideRight {
    0% {
        transform: translateX(-4px);
    }
    50% {
        transform: translateX(4px);
    }
    100% {
        transform: translateX(0);
    }
}

@keyframes slideLeft {
    0% {
        transform: translateX(4px);
    }
    50% {
        transform: translateX(-4px);
    }
    100% {
        transform: translateX(0);
    }
}

/* Conteneur des toggles */
.toggle-switch {
    gap: 0.5rem;
}

/* Tous les boutons toggle */
.toggle-switch-button {
    will-change: transform, box-shadow, background-color;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toggle-switch-bordered .toggle-switch-button:first-child {
    border-top-left-radius: 9999px;
    border-bottom-left-radius: 9999px;
}

.toggle-switch-bordered .toggle-switch-button:last-child {
    border-top-right-radius: 9999px;
    border-bottom-right-radius: 9999px;
}

/* Animation au moment du clic - bouton gauche */
.toggle-switch-button[data-edge='left'][class*='bg-primary'] {
    animation: slideLeft 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Animation au moment du clic - bouton droit */
.toggle-switch-button[data-edge='right'][class*='bg-primary'] {
    animation: slideRight 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Hover sur boutons inactifs */
.toggle-switch-button:not([class*='bg-primary']):hover {
    transform: translateY(-2px);
}
</style>
